import { createReducer } from "utils/AppsmithUtils";
import {
  ReduxAction,
  ReduxActionTypes,
  ReduxActionErrorTypes,
  ApplicationPayload,
} from "constants/ReduxActionConstants";
import { Organization } from "constants/orgConstants";
import { ERROR_MESSAGE_CREATE_APPLICATION } from "constants/messages";
import { UpdateApplicationRequest } from "api/ApplicationApi";
import { CreateApplicationFormValues } from "pages/Applications/helpers";

const initialState: ApplicationsReduxState = {
  isFetchingApplications: false,
  isSavingAppName: false,
  isFetchingApplication: false,
  isChangingViewAccess: false,
  applicationList: [],
  creatingApplication: {},
  deletingApplication: false,
  duplicatingApplication: false,
  userOrgs: [],
  prevApplication: "",
};

const applicationsReducer = createReducer(initialState, {
  [ReduxActionTypes.DELETE_APPLICATION_INIT]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ applicationId: string; orgId: string }>,
  ) => {
    return { ...state, deletingApplication: true };
  },
  [ReduxActionTypes.DELETE_APPLICATION_SUCCESS]: (
    state: ApplicationsReduxState,
    action: ReduxAction<ApplicationPayload>,
  ) => {
    const _organizations = state.userOrgs.map((org: Organization) => {
      if (org.organization.id === action.payload.organizationId) {
        let applications = org.applications;

        applications = applications.filter(
          (application: ApplicationPayload) => {
            return application.id !== action.payload.id;
          },
        );

        return {
          ...org,
          applications,
        };
      }

      return org;
    });

    return {
      ...state,
      userOrgs: _organizations,
      deletingApplication: false,
    };
  },
  [ReduxActionTypes.DELETE_APPLICATION_ERROR]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ orgId: string }>,
  ) => {
    return { ...state, deletingApplication: false };
  },
  [ReduxActionTypes.CHANGE_APPVIEW_ACCESS_INIT]: (
    state: ApplicationsReduxState,
  ) => ({ ...state, isChangingViewAccess: true }),
  [ReduxActionTypes.UPDATE_APPLICATION_COLOR]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ id: string; color: any }>,
  ) => {
    return {
      ...state,
      currentApplication: {
        ...state.currentApplication,
        color: action.payload.color,
      },
    };
  },
  [ReduxActionTypes.UPDATE_APPLICATION_SETTINGS]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ id: string; settings_data: any }>,
  ) => {
    return {
      ...state,
      currentApplication: {
        ...state.currentApplication,
        settings_data: action.payload.settings_data,
      },
    };
  },
  [ReduxActionTypes.UPDATE_APPLICATION_AUTH_MODE]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ id: string; auth_mode_setting: any }>,
  ) => {
    return {
      ...state,
      currentApplication: {
        ...state.currentApplication,
        auth_mode_setting: action.payload.auth_mode_setting,
      },
    };
  },
  [ReduxActionTypes.UPDATE_APPLICATION_AUTH]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ id: string; is_custom_auth: boolean }>,
  ) => {
    return {
      ...state,
      currentApplication: {
        ...state.currentApplication,
        is_custom_auth: action.payload.is_custom_auth,
      },
    };
  },
  [ReduxActionTypes.UPDATE_GLOBAL_APPLICATION_SETTINGS]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ id: string; name: string; value: any }>,
  ) => {
    return {
      ...state,
      currentApplication: {
        ...state.currentApplication,
        global_setting: {
          ...state?.currentApplication?.global_setting,
          [action.payload.name]: action.payload.value,
        },
      },
    };
  },

  [ReduxActionTypes.CHANGE_APPVIEW_ACCESS_SUCCESS]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ id: string; isPublic: boolean }>,
  ) => {
    return {
      ...state,
      isChangingViewAccess: false,
      currentApplication: {
        ...state.currentApplication,
        isPublic: action.payload.isPublic,
      },
    };
  },
  [ReduxActionTypes.GET_ALL_APPLICATION_INIT]: (
    state: ApplicationsReduxState,
  ) => ({ ...state, isFetchingApplications: true }),
  [ReduxActionTypes.FETCH_USER_APPLICATIONS_ORGS_SUCCESS]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ applicationList: any }>,
  ) => {
    return {
      ...state,
      isFetchingApplications: false,
      isFetchingApplicationsEnd: true,
      userOrgs: action.payload,
    };
  },

  [ReduxActionTypes.FETCH_APPLICATION_INIT]: (
    state: ApplicationsReduxState,
  ) => ({ ...state, isFetchingApplication: true }),

  [ReduxActionTypes.FETCH_APPLICATION_FIRST_INIT]: (
    state: ApplicationsReduxState,
  ) => ({ ...state, isLoadingFetchingApplicationInit: true }),

  [ReduxActionTypes.FETCH_APPLICATION_SUCCESS]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ applicationList: ApplicationPayload[] }>,
  ) => ({
    ...state,
    currentApplication: action.payload,
    isFetchingApplication: false,
    isLoadingFetchingApplicationInit: false,
  }),
  [ReduxActionTypes.CREATE_APPLICATION_INIT]: (
    state: ApplicationsReduxState,
    action: ReduxAction<CreateApplicationFormValues>,
  ) => {
    const updatedCreatingApplication = { ...state.creatingApplication };
    updatedCreatingApplication[action.payload.orgId] = true;

    return {
      ...state,
      creatingApplication: updatedCreatingApplication,
    };
  },
  [ReduxActionTypes.CREATE_APPLICATION_SUCCESS]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ orgId: string; application: ApplicationPayload }>,
  ) => {
    const _organizations = state.userOrgs.map((org: Organization) => {
      if (org.organization.id === action.payload.orgId) {
        const applications = org.applications;
        applications.push(action.payload.application);
        org.applications = [...applications];
        return {
          ...org,
        };
      }
      return org;
    });

    const updatedCreatingApplication = { ...state.creatingApplication };
    updatedCreatingApplication[action.payload.orgId] = false;

    return {
      ...state,
      creatingApplication: updatedCreatingApplication,
      applicationList: [...state.applicationList, action.payload.application],
      userOrgs: _organizations,
    };
  },
  [ReduxActionErrorTypes.CREATE_APPLICATION_ERROR]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ orgId: string }>,
  ) => {
    const updatedCreatingApplication = { ...state.creatingApplication };
    updatedCreatingApplication[action.payload.orgId] = false;

    return {
      ...state,
      creatingApplication: updatedCreatingApplication,
      createApplicationError: ERROR_MESSAGE_CREATE_APPLICATION,
    };
  },
  [ReduxActionTypes.SEARCH_APPLICATIONS]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ keyword?: string }>,
  ) => {
    return {
      ...state,
      searchKeyword: action.payload.keyword,
    };
  },
  [ReduxActionTypes.DUPLICATE_APPLICATION_INIT]: (
    state: ApplicationsReduxState,
  ) => {
    return { ...state, duplicatingApplication: true };
  },
  [ReduxActionTypes.DUPLICATE_APPLICATION_SUCCESS]: (
    state: ApplicationsReduxState,
    action: ReduxAction<ApplicationPayload>,
  ) => {
    return {
      ...state,
      duplicatingApplication: false,
      applicationList: [...state.applicationList, action.payload],
    };
  },
  [ReduxActionTypes.DUPLICATE_APPLICATION_ERROR]: (
    state: ApplicationsReduxState,
  ) => {
    return { ...state, duplicatingApplication: false };
  },
  [ReduxActionTypes.UPDATE_APPLICATION]: (
    state: ApplicationsReduxState,
    action: ReduxAction<UpdateApplicationRequest>,
  ) => {
    let isSavingAppName = false;
    const _organizations = state.userOrgs.map(
      (org: Organization, index: number) => {
        const appIndex = org.applications.findIndex(
          app => app.id === action.payload.id,
        );
        const { id, ...rest } = action.payload;
        if (appIndex !== -1) {
          isSavingAppName = action.payload.name !== undefined;
          org.applications[appIndex] = {
            ...org.applications[appIndex],
            ...rest,
          };
        }

        return org;
      },
    );

    return {
      ...state,
      userOrgs: _organizations,
      isSavingAppName: true,
    };
  },
  [ReduxActionTypes.UPDATE_APPLICATION_SUCCESS]: (
    state: ApplicationsReduxState,
  ) => {
    return { ...state, isSavingAppName: false };
  },
  [ReduxActionErrorTypes.UPDATE_APPLICATION_ERROR]: (
    state: ApplicationsReduxState,
  ) => {
    return { ...state, isSavingAppName: false };
  },
  // [ReduxActionTypes.PREV_APP]: (
  //   state: ApplicationsReduxState,
  //   action: ReduxAction<{ prevApplication: string }>,
  // ) => {
  //   return { ...state, prevApplication: action.payload };
  // },
  [ReduxActionTypes.CUR_APP]: (
    state: ApplicationsReduxState,
    action: ReduxAction<{ prevApplication: string }>,
  ) => {
    return { ...state, curApplication: action.payload };
  },
});

export type creatingApplicationMap = Record<string, boolean>;

export interface ApplicationsReduxState {
  applicationList: ApplicationPayload[];
  searchKeyword?: string;
  isFetchingApplications: boolean;
  isSavingAppName: boolean;
  isFetchingApplication: boolean;
  isChangingViewAccess: boolean;
  creatingApplication: creatingApplicationMap;
  createApplicationError?: string;
  deletingApplication: boolean;
  duplicatingApplication: boolean;
  currentApplication?: ApplicationPayload;
  prevApplication?: string;
  curApplication?: string;
  userOrgs: Organization[];
}

export interface Application {
  id: string;
  name: string;
  organizationId: string;
  isPublic: boolean;
  appIsExample: boolean;
  new: boolean;
  pageCount: number;
  defaultPageId: string;
  pages: Array<{ id: string; isDefault: boolean; default: boolean }>;
  userPermissions: string[];
}

export default applicationsReducer;
